import React from 'react';
import PropTypes from 'prop-types';
import Hyperlink from '../../Hyperlink';
import './Fullwidth.css';

/* Section padding: DS token shorthand ('medium-l', 'large-m', …) or a raw CSS value */
const resolveSpace = (value) => (
  typeof value === 'string' && /^(small|medium|large)-(s|m|l)$/.test(value) ? `var(--space-ui-${value})` : value
);

/**
 * Fullwidth Component
 *
 * Full-width content block with optional image, subtitle, main copy,
 * additional copy, tags and a hyperlink CTA.
 *
 * Figma node: 3869:104
 *
 * Props control visibility of each section.
 * Content props override default placeholder values.
 */

const Fullwidth = ({
  // Visibility toggles
  showImage = true,
  showSubtitle = true,
  showMainCopy = true,
  showTags = true,
  showHyperlink = true,

  // Content
  image = null,
  imageAlt = '',
  subtitle = '',
  mainCopy = '',
  additionalCopy = '',
  tags = [],
  hyperlinkLabel = 'Discover More',
  hyperlinkHref = '#',
  onHyperlinkClick,

  // Variants
  mainCopyVariant = 'h3',

  // Style
  backgroundColor = null,
  paddingTop = null,
  paddingBottom = null,
  className = '',
}) => {
  const blockStyle = {
    ...(backgroundColor && { backgroundColor }),
    ...(paddingTop != null && { '--fullwidth-pt': resolveSpace(paddingTop) }),
    ...(paddingBottom != null && { '--fullwidth-pb': resolveSpace(paddingBottom) }),
  };

  return (
    <section
      className={`fullwidth-block${className ? ` ${className}` : ''}`}
      style={blockStyle}
    >
      <div className="fullwidth-block__container">
        {showImage && (
          <div className="fullwidth-block__image-wrapper">
            {image && (
              <img
                className="fullwidth-block__image"
                src={image}
                alt={imageAlt}
              />
            )}
          </div>
        )}

        <div className="fullwidth-block__content-wrapper">
          <div className="fullwidth-block__content">
            {showSubtitle && subtitle && (
              <div className="fullwidth-block__subtitle-row">
                <p className="typo-h5 fullwidth-block__subtitle">{subtitle}</p>
              </div>
            )}

            {showMainCopy && mainCopy && (
              <div className="fullwidth-block__main-copy-row">
                <p className={`${mainCopyVariant === 'h2' ? 'typo-h2' : 'typo-h3'} fullwidth-block__main-copy`}>{mainCopy}</p>
              </div>
            )}

            {additionalCopy && (
              <div className="fullwidth-block__additional-copy-row">
                <p className="typo-body-lg fullwidth-block__additional-copy">{additionalCopy}</p>
              </div>
            )}

            {showTags && tags && tags.length > 0 && (
              <div className="fullwidth-block__tags-row">
                {tags.map((tag, index) => (
                  <span key={index} className="typo-body fullwidth-block__tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {showHyperlink && hyperlinkLabel && (
              <div className="fullwidth-block__hyperlink-row">
                <Hyperlink
                  href={hyperlinkHref}
                  variant="regular"
                  showIconRight
                  onClick={onHyperlinkClick}
                >
                  {hyperlinkLabel}
                </Hyperlink>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

Fullwidth.propTypes = {
  // Visibility
  showImage: PropTypes.bool,
  showSubtitle: PropTypes.bool,
  showMainCopy: PropTypes.bool,
  showTags: PropTypes.bool,
  showHyperlink: PropTypes.bool,

  // Variants
  /** Typography variant for main copy: 'h2' or 'h3' */
  mainCopyVariant: PropTypes.oneOf(['h2', 'h3']),

  // Content
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  subtitle: PropTypes.string,
  mainCopy: PropTypes.string,
  additionalCopy: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  hyperlinkLabel: PropTypes.string,
  hyperlinkHref: PropTypes.string,
  onHyperlinkClick: PropTypes.func,

  // Style
  backgroundColor: PropTypes.string,
  /** Section padding-top: DS token shorthand ('medium-l', 'large-m', …) or raw CSS. Default: large-m */
  paddingTop: PropTypes.string,
  /** Section padding-bottom: DS token shorthand or raw CSS. Default: large-m */
  paddingBottom: PropTypes.string,
  className: PropTypes.string,
};

export default Fullwidth;
